import {Box, HBox} from '../../../components/core/view';
import {
  CustomText,
  H5Text,
  H6Text,
  PSmallText,
} from '../../../components/core/text';
import {
  OrderItemStatus,
  OrderItems,
  itemsSortBy,
} from '../../../batchDetails/types';
import React, {ReactElement, useCallback, useMemo, useState} from 'react';
import Svg, {LinearGradient, Rect, Stop} from 'react-native-svg';
import {
  batchDetailActionsLocal,
  useBatchDetailContext,
} from '@buncha/batchDetails/hooks/batchDetail';
import {formatCurrency, getWidth, isItemOrganic} from '../../../utils/common';
import {getClientType, getStoreName} from '@buncha/common/types/common';
import {quantityPillStyles, styles} from './styles';
import {useColorThemeType, useFontTheme} from '../../../appStyles/hooks/theme';

import {Analytics} from '@buncha/config/analytics/analytics';
import {ChevronRightIcon} from 'native-base';
import EditIcon from '@buncha/components/core/icon/EditIcon';
import {EventType} from '@buncha/config/analytics/type';
import If from '@buncha/components/conditional/If';
import ImageWithQuantityPill from '../../../common/components/ImageWithQuantityPill';
import {ItemDetailsScreen} from '@buncha/itemDetails/navigation/types';
import ItemName from '../ItemName';
import ItemTypePill from '../ItemName/ItemTypePill';
import LocationIcon from '@buncha/components/core/icon/LocationIcon';
import LocationTypo from '../LocationTypo';
import {Modules} from '@buncha/utils/types';
import Notes from './Notes';
import {OrderItem} from '@buncha/home/types';
import QuantityPill from './OrganicPillWithQuantity';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {Touchable} from '../../../components/core/touchable';
import UpdateQuantitySheet from '@buncha/itemDetails/components/ItemDetail/UpdateQuantitySheet';
import {navigation} from '@buncha/config/navigation';
import {useGetLocale} from '@buncha/localization/hooks';
import {useGetRunForBatchDetail} from '@buncha/batchDetails/hooks/BatchDetails';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useUpdateOrderItemQuantity} from '@buncha/itemDetails/hooks/ItemDetails';

interface BatchOrderItemProps {
  rightIcon?: ReactElement<any, any>;
  orderItem: OrderItems;
  onClick: (orderItem: OrderItems) => void;
  disableOnclick?: boolean;
  esIndex: string;
  rightComponent?: React.ReactNode;
  orderPackedStatus?: boolean;
  disabledOpacity?: boolean;
  isRecheckItem?: boolean;
  runId: number | undefined;
  hideLocation?: boolean;
  storeId?: number;
  enableGesture?: boolean;
}

function BatchOrderItem(props: BatchOrderItemProps) {
  const {
    rightIcon,
    orderItem,
    onClick,
    disableOnclick,
    rightComponent,
    orderPackedStatus,
    disabledOpacity,
  } = props;
  const theme = useColorThemeType();
  const runId = props?.runId || 0;
  const [getRunForBatchDetail] = useGetRunForBatchDetail(null);
  const muliFont = useFontTheme('muli');
  const {state, dispatch} = useBatchDetailContext();
  const {body} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};
  const getRightIcon = useCallback(() => {
    if (rightComponent) return rightComponent;
    if (rightIcon) return rightIcon;
    return <ChevronRightIcon color={theme?.text.gray[100]} />;
  }, [rightIcon, theme?.text.gray, rightComponent]);

  const isMeijerStore = useIsMeijerStore(props?.storeId);
  const updatedPrice =
    orderItem?.shopperItemChangesForShopper?.updatedPricePerQuantity;
  const price = updatedPrice || orderItem?.price;
  const quantity =
    orderItem?.quantity ||
    orderItem?.shopperItemChangesForShopper?.updatedQuantity;
  const isNotes = orderItem.note;
  const isOrganic = isItemOrganic(orderItem?.name);

  const dropped = orderItem.itemStatus === OrderItemStatus.dropped;
  const disable = dropped || disableOnclick;
  const opacity = {
    opacity: dropped || disabledOpacity ? 0.5 : 1,
  };
  const isCustomItem = props?.orderItem?.isCustomItem;

  const [showQtyUpdateSheet, setShowQtyUpdateSheet] = useState(false);
  const {updateOrderItemQuantity, info} = useUpdateOrderItemQuantity();

  const getQuantity = useMemo(() => {
    if (isCustomItem) return quantity;
    if (
      state.sortBarValue === itemsSortBy.category &&
      state.showSortBar &&
      !(props?.isRecheckItem ?? false)
    )
      return orderItem?.totalQuantityInThisRun;
    return quantity;
  }, [
    isCustomItem,
    orderItem?.totalQuantityInThisRun,
    props?.isRecheckItem,
    quantity,
    state.showSortBar,
    state.sortBarValue,
  ]);

  const onItemClick = useCallback(() => {
    onClick(orderItem);
  }, [onClick, orderItem]);

  const toggleEditQuantity = useCallback(() => {
    setShowQtyUpdateSheet(!showQtyUpdateSheet);
  }, [showQtyUpdateSheet]);

  const handleUpdateQuantity = useCallback(
    async (updatedQty: number) => {
      Analytics.eventWithProps('Update quantity', {
        type: EventType.input,
        runId,
        orderItemId: orderItem.id,
        catalogItemId: orderItem.catalogItemId,
        storeIndex: props.esIndex,
        previousQuantity: quantity,
        quantity: updatedQty,
        itemName: orderItem.name,
        client: getClientType(isMeijerStore),
        source: 'Order Packing Page',
        storeType: getStoreName(String(props?.storeId)),
      });
      await updateOrderItemQuantity(
        {
          runId,
          orderItemId: orderItem.id,
          itemQuantity: Number(updatedQty),
        },
        (updateOrderItemQuantityRes: OrderItem) => {
          if (updateOrderItemQuantityRes && dispatch)
            dispatch(
              batchDetailActionsLocal.updateDoneSectionWithUpdated({
                orderId: orderItem?.orderId,
                orderItemId: orderItem?.id,
                updatedQty:
                  updateOrderItemQuantityRes?.quantity || Number(updatedQty),
              }),
            );
        },
      );
      toggleEditQuantity();
      getRunForBatchDetail(runId);
    },
    [
      dispatch,
      getRunForBatchDetail,
      isMeijerStore,
      orderItem.catalogItemId,
      orderItem.id,
      orderItem.name,
      orderItem?.orderId,
      props.esIndex,
      props?.storeId,
      quantity,
      runId,
      toggleEditQuantity,
      updateOrderItemQuantity,
    ],
  );

  const navigateToScan = useCallback(() => {
    navigation.navigate(ShopperScreens.ItemDetails, {
      screen: ItemDetailsScreen.ItemScanner,
      params: {
        orderItemId: orderItem?.id,
        storeIndex: props.esIndex,
        runId,
        quantity,
        source: 'Item Cell',
      },
    });
  }, [orderItem?.id, props.esIndex, quantity, runId]);

  return (
    <Box>
      <HBox
        // eslint-disable-next-line react-native/no-inline-styles
        style={{
          pointerEvents: props?.disabledOpacity ? 'none' : undefined,
        }}>
        <Touchable
          style={[styles.mainSubContainer, opacity]}
          disabled={disable}
          onPress={onItemClick}>
          <HBox>
            <Box>
              <ImageWithQuantityPill
                imageLink={orderItem?.itemImageUrl}
                quantity={getQuantity}
                isNSCItem={orderItem?.isNSC2Item}
                totalWeight={orderItem?.orderItemAttribute?.weight}
                unit={orderItem?.orderItemAttribute?.units}
              />
            </Box>
            <Box style={[styles.textContainer]}>
              <HBox style={styles.pillContainer}>
                <If
                  condition={
                    !disabledOpacity && props?.storeId && !isMeijerStore
                  }>
                  <QuantityPill orderItemUnit={orderItem.itemUnit} />
                </If>
                <If condition={disabledOpacity || orderItem.isAdjacentOOSItem}>
                  <Box style={quantityPillStyles.containerBox}>
                    <HBox
                      style={[
                        quantityPillStyles.highlightText,
                        {
                          backgroundColor: theme?.background?.orange?.[100],
                        },
                      ]}>
                      <PSmallText
                        style={[
                          {
                            fontFamily: muliFont[700].normal,
                            color: theme?.text?.white?.[100],
                          },
                        ]}>
                        {body?.passedOff}
                      </PSmallText>
                    </HBox>
                  </Box>
                </If>
                <If condition={isOrganic && !isMeijerStore}>
                  <ItemTypePill isOrganic={isOrganic} />
                </If>
              </HBox>
              <If condition={orderItem?.itemLocation}>
                <Box style={styles.container}>
                  <Svg style={styles.gradient} height="100%" width="100%">
                    <LinearGradient id="grad" x1="0" y1="0" x2="1" y2="0">
                      <Stop
                        offset="0"
                        stopColor={theme?.background.blue[200]}
                        stopOpacity="1"
                      />
                      <Stop
                        offset="1"
                        stopColor={theme?.background.blue[200]}
                        stopOpacity="0"
                      />
                    </LinearGradient>
                    <Rect
                      x="0"
                      y="0"
                      width="100%"
                      height="100%"
                      fill="url(#grad)"
                    />
                  </Svg>
                  <HBox style={[styles.itemLocationContainer]}>
                    <LocationIcon
                      viewBox="-2 0 32 20"
                      fill={theme?.icon?.blue?.[300]}
                    />
                    <H5Text
                      style={{
                        color: theme?.text?.blue?.[300],
                        fontFamily: muliFont?.[900]?.normal,
                      }}>
                      {orderItem?.itemLocation}
                    </H5Text>
                  </HBox>
                </Box>
              </If>
              <If condition={isMeijerStore}>
                <H6Text
                  style={[
                    styles.textHeader,
                    {
                      fontFamily: muliFont[700].normal,
                      color: theme?.text.blue[300],
                    },
                  ]}>
                  {orderItem?.name}
                </H6Text>
              </If>
              <If condition={!isMeijerStore}>
                <ItemName name={orderItem?.name} />
              </If>
              <If condition={orderItem?.options}>
                <CustomText
                  numberOfLines={2}
                  style={[
                    styles.textHeader,
                    {
                      fontFamily: muliFont[700].normal,
                      color: theme?.text.blue[100],
                    },
                  ]}>
                  {orderItem?.options}
                </CustomText>
              </If>
              <If condition={rightIcon}>
                <Touchable
                  style={styles.editQtyBox}
                  onPress={toggleEditQuantity}
                  disabled={dropped || orderPackedStatus}>
                  <CustomText
                    style={[styles.editQyt, {color: theme?.text.blue[100]}]}>
                    {body?.editQty}
                  </CustomText>
                  <EditIcon
                    viewBox="0 0 15 15"
                    width={Number(getWidth(15))}
                    height={15}
                    fill={theme?.text.blue[100]}
                  />
                </Touchable>
              </If>
              <H6Text
                style={[
                  styles.priceText,
                  {
                    fontFamily: muliFont[700].normal,
                    color: theme?.text.gray[100],
                  },
                ]}>
                {formatCurrency(price * quantity)}
              </H6Text>
              <If condition={isNotes}>
                <Notes orderItemNotes={orderItem.note} />
              </If>
            </Box>
          </HBox>
        </Touchable>
        <If condition={props.enableGesture}>
          <Touchable onPress={navigateToScan}>
            <Box>{getRightIcon()}</Box>
          </Touchable>
        </If>
        <If condition={!props.enableGesture}>
          <Box>{getRightIcon()}</Box>
        </If>
      </HBox>
      <UpdateQuantitySheet
        open={showQtyUpdateSheet}
        onClose={toggleEditQuantity}
        quantity={getQuantity}
        handleUpdateQuantity={handleUpdateQuantity}
        loading={info?.loading}
        itemName={orderItem.name}
        itemImageUrl={orderItem.itemImageUrl}
        itemPrice={price}
      />
      <If condition={!props.hideLocation}>
        <If
          condition={
            (orderItem?.itemLocationInformation?.aisle ||
              orderItem?.itemLocationInformation?.storeContext) &&
            !orderItem?.itemLocation
          }>
          <LocationTypo
            aisle={orderItem?.itemLocationInformation?.aisle ?? ''}
            context={orderItem?.itemLocationInformation?.storeContext ?? ''}
            orderItemId={orderItem?.id}
            esIndex={props.esIndex}
          />
        </If>
      </If>
    </Box>
  );
}

export default React.memo(BatchOrderItem);
